import { formatStorageBytes, PLAN_DISPLAY_NAMES, planDisplayName } from '@/atoms/subscription.atom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { BillingInterval, SubscriptionPlan } from '@/graphql/graphql';
import { usePricing } from '@/hooks/usePricing';
import { useSubscription } from '@/hooks/useSubscription';
import { analytics } from '@/lib/analytics';
import { Check, Mail } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface PlanInfo {
  plan: SubscriptionPlan;
  name: string;
  description: string;
  inherits?: string;
  features: string[];
}

// Selectable plans only — CUSTOM is never shown as a self-service option
const PLAN_ORDER: SubscriptionPlan[] = ['FREE', 'STARTER', 'TEAM', 'BUSINESS'];

// TODO(owner): confirm sales contact email
const SALES_CONTACT = 'mailto:hello@skygis.cloud';

interface TierSelectorProps {
  onPlanSelect?: (plan: SubscriptionPlan) => void;
  showChangeButton?: boolean;
}

export const TierSelector = ({ onPlanSelect, showChangeButton = true }: TierSelectorProps) => {
  const { subscription, changePlan, loading } = useSubscription();
  const { formatPrice, getPlanPrice } = usePricing();
  const { t } = useTranslation('subscription');
  const hasScheduledPlan = subscription.scheduledPlan != null;
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);

  const isCustomPlan = subscription.isCustom || subscription.plan === 'CUSTOM';
  const currentPlanIndex = PLAN_ORDER.indexOf(subscription.plan);
  const billingInterval: BillingInterval = subscription.billingInterval || 'MONTHLY';

  // TODO (later task): replace with real plan info from translations
  const PLAN_INFO: PlanInfo[] = [
    {
      plan: 'FREE',
      name: t('tiers.free.name', { defaultValue: 'Free' }),
      description: t('tiers.free.description', { defaultValue: 'Get started for free' }),
      features: [],
    },
    {
      plan: 'STARTER',
      name: t('tiers.starter.name', { defaultValue: 'Starter' }),
      description: t('tiers.starter.description', { defaultValue: 'For small teams getting started' }),
      inherits: t('tiers.free.name', { defaultValue: 'Free' }),
      features: [],
    },
    {
      plan: 'TEAM',
      name: t('tiers.team.name', { defaultValue: 'Team' }),
      description: t('tiers.team.description', { defaultValue: 'For growing teams' }),
      inherits: t('tiers.starter.name', { defaultValue: 'Starter' }),
      features: [],
    },
    {
      plan: 'BUSINESS',
      name: t('tiers.business.name', { defaultValue: 'Business' }),
      description: t('tiers.business.description', { defaultValue: 'For larger organizations' }),
      inherits: t('tiers.team.name', { defaultValue: 'Team' }),
      features: [],
    },
  ];

  const handlePlanSelect = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    onPlanSelect?.(plan);
  };

  const handleChangePlan = async () => {
    if (!selectedPlan || selectedPlan === subscription.plan) return;

    const selectedIndex = PLAN_ORDER.indexOf(selectedPlan);
    const isUpgrade = selectedIndex > currentPlanIndex;

    const id = toast.loading(
      isUpgrade
        ? t('tierSelector.upgrading', { tier: PLAN_DISPLAY_NAMES[selectedPlan] })
        : t('tierSelector.schedulingDowngrade', { tier: PLAN_DISPLAY_NAMES[selectedPlan] }),
    );

    try {
      await changePlan(selectedPlan);
      analytics.capture('Subscription Plan Changed', {
        plan: selectedPlan,
        action: isUpgrade ? 'upgrade' : 'downgrade',
      });
      toast.success(
        isUpgrade
          ? t('tierSelector.upgradedSuccess', { tier: PLAN_DISPLAY_NAMES[selectedPlan] })
          : t('tierSelector.downgradeScheduled', { tier: PLAN_DISPLAY_NAMES[selectedPlan] }),
        { id },
      );
      setSelectedPlan(null);
    } catch (err) {
      toast.error(t('tierSelector.failedChangeTier'), { id });
    }
  };

  const getPlanAction = (plan: SubscriptionPlan) => {
    const planIndex = PLAN_ORDER.indexOf(plan);

    if (plan === subscription.plan) {
      return { label: t('tierSelector.currentPlan'), disabled: true, variant: 'outline' as const };
    }
    if (hasScheduledPlan && plan === subscription.scheduledPlan) {
      return { label: t('tierSelector.scheduled'), disabled: true, variant: 'outline' as const };
    }
    if (planIndex > currentPlanIndex) {
      return { label: t('tierSelector.upgrade'), disabled: false, variant: 'default' as const };
    }
    return { label: t('tierSelector.downgrade'), disabled: false, variant: 'secondary' as const };
  };

  // Read-only panel for custom/negotiated plans
  if (isCustomPlan) {
    const productName = planDisplayName(subscription);
    return (
      <Card>
        <CardHeader>
          <CardTitle>{productName}</CardTitle>
          <CardDescription>{t('tierSelector.customManaged')}</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='grid grid-cols-2 gap-3 text-sm'>
            <div className='rounded-lg border border-slate-200 dark:border-stone-800 p-3'>
              <p className='text-xs text-slate-500 dark:text-stone-400 mb-1'>{t('subscription.seatsLabel')}</p>
              <p className='font-semibold text-slate-900 dark:text-white'>
                {subscription.seatsUsed} / {subscription.maxSeats}
              </p>
            </div>
            <div className='rounded-lg border border-slate-200 dark:border-stone-800 p-3'>
              <p className='text-xs text-slate-500 dark:text-stone-400 mb-1'>{t('settings.storage')}</p>
              <p className='font-semibold text-slate-900 dark:text-white'>
                {formatStorageBytes(subscription.storageLimitBytes)}
              </p>
            </div>
          </div>
          <div className='flex items-center justify-between rounded-lg border border-slate-200 dark:border-stone-800 bg-slate-50 dark:bg-stone-900/50 p-3 gap-3'>
            <p className='text-sm text-slate-600 dark:text-stone-300'>{t('tierSelector.customManaged')}</p>
            <Button variant='outline' size='sm' className='shrink-0' asChild>
              <a href={SALES_CONTACT}>
                <Mail className='h-3.5 w-3.5 mr-1.5' />
                {t('tierSelector.contactCta')}
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('tierSelector.title')}</CardTitle>
        <CardDescription>{t('tierSelector.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className='grid grid-cols-2 gap-3'>
          {PLAN_INFO.map((planInfo) => {
            const action = getPlanAction(planInfo.plan);
            const isSelected = selectedPlan === planInfo.plan;
            const isCurrent = planInfo.plan === subscription.plan;
            const isScheduled = hasScheduledPlan && planInfo.plan === subscription.scheduledPlan;

            return (
              <div
                key={planInfo.plan}
                className={`relative rounded-lg border p-3 transition-all ${
                  isSelected
                    ? 'border-blue-500 ring-2 ring-blue-500 cursor-pointer'
                    : isScheduled
                      ? 'border-blue-400/60 bg-blue-50/50 dark:bg-blue-950/10 cursor-pointer'
                      : isCurrent
                        ? 'border-green-500/60 bg-green-50/50 dark:bg-green-950/10 cursor-pointer'
                        : 'border-slate-200 dark:border-stone-800 hover:border-slate-300 dark:hover:border-stone-700 cursor-pointer'
                }`}
                onClick={() => !action.disabled && handlePlanSelect(planInfo.plan)}>
                {planInfo.plan === 'TEAM' && currentPlanIndex < PLAN_ORDER.indexOf('TEAM') && (
                  <Badge
                    className='absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] px-1.5 py-0'
                    variant='default'>
                    {t('tierSelector.recommended')}
                  </Badge>
                )}

                <div className='flex flex-col h-full gap-2'>
                  <div>
                    <h3 className='text-sm font-semibold text-slate-900 dark:text-white'>{planInfo.name}</h3>
                    <p className='text-xs text-slate-500 dark:text-stone-400'>{planInfo.description}</p>
                  </div>

                  {/* Pricing */}
                  <div>
                    <div className='flex items-baseline gap-1'>
                      <span className='text-lg font-bold text-slate-900 dark:text-white'>
                        {formatPrice(getPlanPrice(planInfo.plan, billingInterval))}
                      </span>
                      <span className='text-xs text-slate-400 dark:text-stone-500'>
                        /{billingInterval === 'MONTHLY' ? 'mo' : 'yr'}
                      </span>
                    </div>
                  </div>

                  {planInfo.inherits && (
                    <p className='text-[11px] text-slate-400 dark:text-stone-500 font-medium'>
                      {t('tierSelector.everythingIn', { tier: planInfo.inherits })}
                    </p>
                  )}

                  <ul className='space-y-1'>
                    {planInfo.features.map((feature, index) => (
                      <li key={index} className='flex items-center gap-1.5 text-xs text-slate-600 dark:text-stone-300'>
                        <Check className='h-3 w-3 shrink-0 text-green-500' />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {showChangeButton && (
                    <Button
                      variant={action.variant}
                      size='sm'
                      className='w-full mt-auto h-7 text-xs'
                      disabled={action.disabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlanSelect(planInfo.plan);
                      }}>
                      {action.label}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Contact us CTA card — non-selectable, for Professional/custom deals */}
          <div className='relative rounded-lg border border-slate-200 dark:border-stone-800 p-3 flex flex-col gap-2 bg-slate-50/50 dark:bg-stone-900/30'>
            <div>
              <h3 className='text-sm font-semibold text-slate-900 dark:text-white'>{t('tierSelector.contactCta')}</h3>
              <p className='text-xs text-slate-500 dark:text-stone-400 mt-0.5'>{t('tierSelector.contactBody')}</p>
            </div>
            <Button variant='outline' size='sm' className='w-full mt-auto h-7 text-xs' asChild>
              <a href={SALES_CONTACT}>
                <Mail className='h-3 w-3 mr-1.5' />
                {t('tierSelector.contactCta')}
              </a>
            </Button>
          </div>
        </div>

        {/* Scheduled change banner */}
        {hasScheduledPlan && (
          <div className='mt-4 flex items-center justify-between rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 p-3'>
            <div>
              <p className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                {t('tierSelector.downgradeToScheduled', {
                  tier: PLAN_DISPLAY_NAMES[subscription.scheduledPlan as SubscriptionPlan],
                })}
              </p>
              <p className='text-xs text-blue-600 dark:text-blue-400'>{t('tierSelector.takesEffectAtEnd')}</p>
            </div>
            <Button
              variant='outline'
              size='sm'
              className='shrink-0 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/30'
              disabled={loading}
              onClick={async () => {
                const id = toast.loading(t('tierSelector.cancellingDowngrade'));
                try {
                  await changePlan(subscription.plan);
                  toast.success(t('tierSelector.downgradeCancelled'), { id });
                } catch {
                  toast.error(t('tierSelector.failedCancelChange'), { id });
                }
              }}>
              {loading ? t('tierSelector.processing') : t('tierSelector.cancelDowngrade')}
            </Button>
          </div>
        )}

        {/* Plan change confirmation dialog (upgrade & downgrade) */}
        {(() => {
          const isDialogOpen = showChangeButton && !!selectedPlan && selectedPlan !== subscription.plan;
          const isUpgrading = isDialogOpen && PLAN_ORDER.indexOf(selectedPlan!) > currentPlanIndex;

          return (
            <AlertDialog
              open={isDialogOpen}
              onOpenChange={(open) => {
                if (!open) setSelectedPlan(null);
              }}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {isUpgrading
                      ? t('tierSelector.upgradeTo', { tier: selectedPlan ? PLAN_DISPLAY_NAMES[selectedPlan] : '' })
                      : t('tierSelector.downgradeTo', { tier: selectedPlan ? PLAN_DISPLAY_NAMES[selectedPlan] : '' })}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {isUpgrading ? t('tierSelector.upgradeAppliedImmediately') : t('tierSelector.downgradeTakesEffect')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('tierSelector.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleChangePlan} disabled={loading}>
                    {loading
                      ? t('tierSelector.processing')
                      : isUpgrading
                        ? t('tierSelector.confirmUpgrade')
                        : t('tierSelector.confirmDowngrade')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          );
        })()}
      </CardContent>
    </Card>
  );
};
